import React, { FC } from 'react';

import { FormattedMessage } from 'react-intl';

import Badge from '@atlaskit/badge';
import { Box, xcss } from '@atlaskit/primitives';
import Tabs, { TabList, TabPanel, useTab } from '@atlaskit/tabs';
import Tooltip from '@atlaskit/tooltip-18';

import { useIntl } from 'src/hooks/intl';

import { MergeCheckModulesTable } from './merge-check-modules-table';
import { sharedMessages } from './merge-checks-tabs.i18n';
import { contextMessages } from './messages.i18n';
import { DescriptionContainer, TabLabel, TableContainer } from './styled';
import {
  BranchConfig,
  BranchConfigKey,
  CheckModuleId,
  CheckModulesConfig,
  MergeCheckConfigurationState,
  ForgeMergeCheckModule,
  MergeChecksContext,
} from './types';
import { urls } from './urls';

const tabWithBadgeStyles = xcss({
  display: 'flex',
  flexDirection: 'row',
});

const TabWithBadge: FC<{ label: string; enabledChecksCount: number }> = ({
  label,
  enabledChecksCount,
}) => {
  const tabAttributes = useTab();
  const { formatMessage } = useIntl();

  return (
    <Box
      {...tabAttributes}
      xcss={tabWithBadgeStyles}
      aria-label={formatMessage(sharedMessages.configurationTabsAriaLabel, {
        tabLabel: label,
        enabledChecks:
          enabledChecksCount > 0
            ? formatMessage(sharedMessages.enabledChecksTooltip, {
                count: enabledChecksCount,
              })
            : '',
      })}
    >
      <TabLabel>{label}</TabLabel>
      {enabledChecksCount > 0 && (
        <Tooltip
          testId="merge-checks-tabs-tooltip"
          position="top"
          content={formatMessage(sharedMessages.enabledChecksTooltip, {
            count: enabledChecksCount,
          })}
        >
          <Badge testId="merge-checks-tabs-badge">{enabledChecksCount}</Badge>
        </Tooltip>
      )}
    </Box>
  );
};

type MergeChecksTabsProps = {
  context: MergeChecksContext;
  checkModulesConfig?: CheckModulesConfig;
  checkIdBeingConfigured?: CheckModuleId;
  onSubmitCheckConfiguration: (
    branchConfig: BranchConfig,
    checkModule: ForgeMergeCheckModule,
    oldState: MergeCheckConfigurationState,
    newState: MergeCheckConfigurationState
  ) => void;
};

export const MergeChecksTabs: FC<MergeChecksTabsProps> = ({
  context,
  checkModulesConfig,
  checkIdBeingConfigured,
  onSubmitCheckConfiguration,
}) => {
  const { formatMessage } = useIntl();

  const renderTab = (branchConfigKey: BranchConfigKey) => {
    const { branchConfig, checkIds } = checkModulesConfig!.enabledChecksMap.get(
      branchConfigKey
    )!;
    const { type, identifier } = branchConfig;
    let label: string = identifier;
    if (type === 'model') {
      switch (identifier) {
        case 'all_branches':
          label = formatMessage(sharedMessages.allBranchesTabLabel);
          break;
        case 'main':
          label = formatMessage(sharedMessages.mainBranchTabLabel);
          break;
      }
    }

    return (
      <TabWithBadge
        key={`${type}${identifier}`}
        label={label}
        enabledChecksCount={checkIds.size}
      />
    );
  };

  const renderTabPanel = (
    branchConfigKey: BranchConfigKey,
    enabledCheckModuleIds: Map<CheckModuleId, boolean>
  ) => {
    const { branchConfig } = checkModulesConfig!.enabledChecksMap.get(
      branchConfigKey
    )!;
    const { type, identifier } = branchConfig;
    return (
      <TabPanel key={`${type}${identifier}`}>
        <TableContainer>
          <MergeCheckModulesTable
            branchConfig={branchConfig}
            installedCheckModules={
              checkModulesConfig!.installedForgeCheckModules
            }
            enabledCheckModuleIds={enabledCheckModuleIds}
            checkIdBeingConfigured={checkIdBeingConfigured}
            onSubmitCheckConfiguration={onSubmitCheckConfiguration}
          />
        </TableContainer>
      </TabPanel>
    );
  };

  return (
    <>
      <DescriptionContainer>
        <p>
          <FormattedMessage
            {...contextMessages[context].description}
            values={{
              learnMoreLink: (
                <a href={urls.learnMore} target="_blank">
                  {formatMessage(contextMessages[context].learnMoreLinkLabel)}
                </a>
              ),
            }}
          />
        </p>
      </DescriptionContainer>
      {checkModulesConfig && (
        <Tabs id="default">
          <TabList>
            {[
              ...checkModulesConfig.enabledChecksMap.keys(),
            ].map(branchConfigKey => renderTab(branchConfigKey))}
          </TabList>
          {[
            ...checkModulesConfig.enabledChecksMap.entries(),
          ].map(([branchConfigKey, enabledMergeCheck]) =>
            renderTabPanel(branchConfigKey, enabledMergeCheck.checkIds)
          )}
        </Tabs>
      )}
    </>
  );
};
